import {defineStore} from 'pinia';
import getDateInfo from '~/composables/date-formatter';
import type {
  ICartPreview,
  ICartPreviewGetter,
  ICartProduct,
  IChangedCartProduct,
  IProductDataOnChange,
  ISummaryProduct
} from '~/shared/interfaces/cart';

interface CartState {
  products: ICartProduct[] | []
  changedProductsList: IChangedCartProduct[] | []
  changedProductsDate: string
  cartPreview: ICartPreview[] | []
  currentProductData: ICartProduct | null
  deleteProductsListType: 'available' | 'unavailable'
  modalContentType: 'delete' | 'comment' | 'changes' | 'download-positions' | 'summary' | 'fast-order'
  showCartModal: boolean
  watchCartChangesType: 'notification' | 'on-open' | 'on-order'
  cartChangesNotificationWatched: boolean
  successOrderNum: string
  successOrderPrice: string
}

const useCartStore = defineStore('cart', {
  state: (): CartState => ({
    products: [],
    changedProductsList: [],
    cartPreview: [],
    currentProductData: null,
    deleteProductsListType: 'available',
    modalContentType: 'delete',
    showCartModal: false,
    watchCartChangesType: 'on-open',
    cartChangesNotificationWatched: true,
    successOrderNum: '',
    successOrderPrice: '',
    changedProductsDate: ''
  }),
  getters: {
    getCartPreview(): ICartPreviewGetter {
      if (this.cartPreview.length) {
        return this.cartPreview.reduce<ICartPreviewGetter>((acc, item) => {
          acc[item.key] = item.count;
          return acc;
        }, {})
      }

      return {};
    },
    getSelectedProducts(state: CartState) {
      return state.products.filter(product => product.selected);
    },
    getSelectedProductsSummary(state: CartState) {
      const summaryProducts = state.products.reduce<{ [key: string]: ISummaryProduct }>((acc, product) => {
        if (product.selected) {
          const summaryProductsKey = product.article + '_' + product.brand.name;

          if (!acc[summaryProductsKey]) {
            acc[summaryProductsKey] = {
              ...product,
              summaryCount: product.count,
              summaryPrice: product.count * product.price,
            }
          } else {
            acc[summaryProductsKey].summaryCount += product.count;
            acc[summaryProductsKey].summaryPrice += product.count * product.price;
          }
        }

        return acc;
      }, {});

      return Object.values(summaryProducts);
    },
    getAvailableProducts(state) {
      return state.products.filter((product: ICartProduct) => product.isAvailable);
    },
    getUnavailableProducts(state) {
      return state.products.filter((product: ICartProduct) => !product.isAvailable);
    },
  },
  actions: {
    resetPreviewList(newPreview: ICartPreview[]) {
      this.cartPreview = newPreview;
    },
    clearPreviewList() {
      this.cartPreview = [];
    },
    setCartPreviewData(newPreview: ICartPreview, isRemove?: boolean) {
      let cartPreviewList: ICartPreview[] = this.cartPreview;

      if (isRemove) {
        cartPreviewList = cartPreviewList.filter(preview => newPreview.key != preview.key);
      } else {
        const previewInList = cartPreviewList.find(preview => newPreview.key == preview.key);

        if (previewInList) {
          previewInList.count = newPreview.count;
        } else {
          cartPreviewList.push(newPreview);
        }
      }
    },
    setNotificationWatchedStatus(watchFlag: boolean) {
      this.cartChangesNotificationWatched = watchFlag;
    },
    setWatchCartChangesType(watchFlag: CartState['watchCartChangesType']) {
      this.watchCartChangesType = watchFlag;
    },
    setCurrentProductData(product: ICartProduct) {
      this.currentProductData = product;
    },
    setDeleteProductsListType(deleteType: CartState['deleteProductsListType']) {
      this.deleteProductsListType = deleteType;
    },
    setModalContentType(contentType: CartState['modalContentType']) {
      this.modalContentType = contentType;
    },
    setShowCartModal(showModal: boolean) {
      this.showCartModal = showModal;
    },
    setProductsList(productsList: ICartProduct[]) {
      this.products = productsList;
    },
    setProductData({ id, count, comment }: IProductDataOnChange) {
      const productForSet = this.products.find(product => product.id == id);
      const savedProducts = localStorage.getItem('cartProducts');

      if (productForSet) {
        productForSet.count = count;
        productForSet.comment = comment;
      }

      if (savedProducts) {
        const savedProductsParsed: ICartProduct[] = JSON.parse(savedProducts);
        const savedProductOnSet = savedProductsParsed.find(product => product.id == id);

        if (savedProductOnSet) {
          savedProductOnSet.count = count;
          savedProductOnSet.comment = comment;

          localStorage.setItem('cartProducts', JSON.stringify(savedProductsParsed));
          localStorage.setItem('cartPreview', JSON.stringify(this.cartPreview))
        }
      }
    },
    deleteProducts(type: CartState['deleteProductsListType']) {
      this.products = this.products.filter(product => {
        if (type == 'available') {
          return !product.selected;
        } else {
          return product.isAvailable;
        }
      })
    },
    deleteProduct(id: ICartProduct['id']) {
      const productIndex = this.products.findIndex(product => product.id == id);
      this.products.splice(productIndex, 1);
      this.cartPreview.splice(productIndex, 1);

      if (localStorage.getItem('cartProducts') && localStorage.getItem('cartPreview')) {
        localStorage.setItem('cartProducts', JSON.stringify(this.products))
        localStorage.setItem('cartPreview', JSON.stringify(this.cartPreview))
      }
    },
    setChangedProductsList(changedProducts: IChangedCartProduct[]) {
      if (changedProducts.length) {
        this.changedProductsList = changedProducts;
        this.setModalContentType('changes');
        this.setWatchCartChangesType('on-open');
        this.setShowCartModal(true);
        this.setNotificationWatchedStatus(false);
      }
    },
    setChangedProductsDate(date: string) {
      this.changedProductsDate = `${getDateInfo(date).dateDay} ${getDateInfo(date).monthName}, ${getDateInfo(date).dateTime}`
    },
    setProductsSelectedStatus(productsIds: ICartProduct['id'][], selectedStatus: boolean) {
      productsIds.forEach(productId => {
        const foundProduct = this.products.find(product => product.id == productId);
        if (foundProduct) {
          foundProduct.selected = selectedStatus;
        }
      });
    },
    setSuccessOrderNum(orderNum: string) {
      this.successOrderNum = orderNum
    },
    setSuccessOrderPrice(orderPrice: string) {
      this.successOrderPrice = orderPrice
    },
  }
});

export default useCartStore;
